import React, { useContext } from 'react';
import { useSubscription } from '@apollo/client';
import styled from 'styled-components';
import { DateTime } from 'luxon';

import Timer from '../Timer';
import AuthContext from 'src/contexts/AuthContext';
import { PrimaryButton, SecondaryButton } from 'src/elements/buttons';
import { upcomingSale, enterableSale } from 'src/utils/upcomingSale';
import { lotsLabel } from 'src/utils/lotsLabel';
import useCarouselIndex from 'src/hooks/useCarouselIndex';
import useSaleRegistration from 'src/hooks/useSaleRegistration';
import { SALE_ACTIVATED } from './queries';
import { textPrimary, elRed } from 'src/styles/colors';
import { ASPECT_PERCENT } from 'src/config/imageConfig';
import fonts from 'src/styles/fonts';
import spacing from 'src/styles/spacing';

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${textPrimary};
  border-radius: 10px;
  box-shadow: 0px 4px 50px rgba(0, 0, 0, 0.22);
  padding: ${({ $large }) => ($large ? spacing.elementGap : spacing.tightGap)};
  gap: ${spacing.tightGap};
  overflow-wrap: break-word;
`;

/* Photo stacked on top on narrow screens; beside the details (text
   left, photo right, top-aligned) from tablet up. row-reverse keeps
   the photo first in the DOM for the stacked layout. */
const BodyRow = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing.tightGap};

  @media (min-width: 768px) {
    flex-direction: row-reverse;
    gap: ${({ $large }) => ($large ? spacing.elementGap : spacing.tightGap)};
  }
`;

/* Same display ratio as asset cards (imageConfig's 4:3 crop). */
const PhotoStrip = styled.img`
  width: 100%;
  aspect-ratio: ${100 / ASPECT_PERCENT};
  object-fit: cover;
  border-radius: 6px;

  @media (min-width: 768px) {
    width: 40%;
    align-self: flex-start;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${spacing.tightGap};
  min-width: 0;
`;

const SaleName = styled.h3`
  margin: 0;
  font-size: ${({ $large }) => ($large ? '24px' : '18px')};
  font-weight: ${fonts.sectionTitle.weight};
`;

const LotsCount = styled.div`
  font-size: ${fonts.body.size};
  font-weight: 700;
  color: ${elRed};
  margin-top: -6px;
`;

const SaleDetails = styled.div`
  font-size: ${fonts.body.size};
`;

const CountdownRow = styled.div`
  align-self: flex-start;
  margin-top: 4px;
`;

const RegistrationError = styled.div`
  font-size: ${fonts.body.size};
  color: ${elRed};
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${spacing.tightGap};

  a,
  button {
    white-space: normal;
    min-width: 0;
  }

  a,
  button {
    flex: 1;
  }

  @media (min-width: 768px) {
    flex-direction: column;

    a,
    button {
      flex: initial;
    }
  }
`;

const SaleCard = ({ sale, large }) => {
  const { isLoggedIn } = useContext(AuthContext);
  const { register, registering, registered, error } = useSaleRegistration(sale.id);

  const photos = sale.previewPhotoUrls || [];
  const [photoIndex] = useCarouselIndex(photos.length);

  // Buyers are here before the clerk opens the room, so the page load cannot
  // be the last word. Apollo merges the push into the cached Sale for us.
  useSubscription(SALE_ACTIVATED, {
    variables: { id: sale.id },
    skip: !upcomingSale(sale),
  });

  // Only the way in waits on the clerk; registering early still goes through.
  const enterButton = () => {
    const started = enterableSale(sale);
    const biddingPath = `/bidding/${sale.id}`;

    if(registering) {
      return { label: 'Registering...', disabled: true };
    }

    if(isLoggedIn && registered) {
      return started
        ? { label: 'Enter Sale', to: biddingPath }
        : { label: 'Registered — Sale Not Started', disabled: true };
    }

    if(isLoggedIn) {
      return {
        label: 'Register to Bid',
        to: started ? biddingPath : null,
        onClick: register
      };
    }

    return {
      label: 'Click to Register for Auction',
      to: '/login',
      target: '_blank'
    };
  };

  const { label: enterButtonLabel, ...enterButtonProps } = enterButton();

  return (
    <CardContainer $large={large}>
      <BodyRow $large={large}>
        {photos.length > 0 &&
          <PhotoStrip src={photos[photoIndex]} alt={`${sale.name} lot`} />
        }
        <ContentColumn>
          <SaleName $large={large}>{sale.name}</SaleName>
          {upcomingSale(sale) ? (
            <>
              <LotsCount>{lotsLabel(sale.visibleSaleAssetsCount)}</LotsCount>
              <SaleDetails>
                LIVE bidding begins{' '}
                {DateTime.fromISO(sale.startAt).toFormat('LLL dd, h:mma')}.{' '}
                Pre bidding available now.
              </SaleDetails>
              <CountdownRow>
                <Timer startDate={sale.startAt} />
              </CountdownRow>
            </>
          ) : (
            <SaleDetails>Closed {sale.endAt}.</SaleDetails>
          )}
        </ContentColumn>
      </BodyRow>
      {upcomingSale(sale) &&
        <>
          <ButtonsContainer>
            <PrimaryButton small={!large} {...enterButtonProps}>
              {enterButtonLabel}
            </PrimaryButton>
            <SecondaryButton small={!large} to={`/sales/${sale.id}`}>
              View Lots
            </SecondaryButton>
          </ButtonsContainer>
          {error && <RegistrationError role="alert">{error}</RegistrationError>}
        </>
      }
    </CardContainer>
  );
};

export default SaleCard;
